import { Modal, Form, DatePicker, Select, Alert, Divider } from 'antd';
import { DATE_FORMAT, TIME_FORMAT } from 'constants/dates';
import dayjs from 'dayjs';
import { useModalStateContext, useUIDispatchContext } from 'providers/UI';
import { FC, memo, useEffect, useMemo, useState } from 'react';
import { updateAppointmentApprovedDate } from 'services/api/appointments';

import { useAppointmentDetail } from 'pages/AppointmentsDetails/AppointmentDetailContext';
import { showErrorMessage } from 'utils/showErrorMessage';

const DateModal: FC = () => {
  // form
  const [form] = Form.useForm();
  // context
  const { dateModal } = useModalStateContext();
  const { toggleModal } = useUIDispatchContext();
  const { appointment, refresh } = useAppointmentDetail();
  const [loading, setLoading] = useState(false);

  const closeModal = () => {
    toggleModal('dateModal', false);
    form.resetFields();
  };

  const date = useMemo(
    () =>
      appointment?.dates?.find(
        item => `${item.id}` === `${dateModal.params?.dateId}`
      ),
    [appointment?.dates, dateModal.params?.dateId]
  );

  useEffect(() => {
    if (dateModal.visible) {
      form.setFieldsValue({
        accepted: date?.accepted ? 1 : 0,
        date: date?.date ? dayjs(date.date) : dayjs()
      });
    }
  }, [date, dateModal.visible, form]);

  const onFinish = async (values: { date: string; accepted: number }) => {
    if (!date?.id) return;

    try {
      setLoading(true);
      await updateAppointmentApprovedDate(date.id, {
        confirm: values.accepted === 1,
        date: dayjs(values.date).format()
      });
      closeModal();
      refresh();
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Select date"
      open={dateModal.visible}
      onOk={form.submit}
      onCancel={closeModal}
      confirmLoading={loading}
      destroyOnClose
    >
      <Alert
        message="Warning"
        description="Please note that only one date can be accepted. If you wish to select a specific date, please be aware that it will overwrite any previously chosen dates."
        type="info"
        showIcon
      />
      <Divider />
      <Form form={form} onFinish={onFinish} layout="vertical">
        <Form.Item
          label="Accepted"
          name="accepted"
          rules={[{ required: true }]}
        >
          <Select
            options={[
              {
                label: 'Yes',
                value: 1
              },
              {
                label: 'No',
                value: 0
              }
            ]}
          />
        </Form.Item>
        <Form.Item label="Date" name="date" rules={[{ required: true }]}>
          <DatePicker
            style={{ width: '100%' }}
            showTime={{ format: 'HH:mm', minuteStep: 5 }}
            format={`${DATE_FORMAT} ${TIME_FORMAT}`}
            disabledDate={date => {
              return date.diff(dayjs(), 'days') < 0;
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default memo(DateModal);
